import { exportExcelMapper } from '../mappers/export-excel.mapper';
import { managerRequestMapper } from '../mappers/group-admin-request.mapper';
import { importFileMapper } from '../mappers/import-file.mapper';
import { learnerImportMapper } from '../mappers/learner-import.mapper';
import { learnerMapper } from '../mappers/learner.mapper';
import { paginationInfoMapper } from '../mappers/pagination-info.mapper';
import { queryParamsMapper } from '../mappers/query-params.mapper';
import { ExportExcel } from '../models/export-excel';
import { Learner } from '../models/learner';
import { LearnerImport } from '../models/learner-import';
import { PaginationInfo } from '../models/pagination-info';
import { User } from '../models/user';
import { appUrlsConfig } from './app-urls.config';
import { axiosService, catchHttpErrorWithValidationSupport } from './axios.service';
import { TableQueryParams, prepareTableQueryParams } from './table-query-params.service';

export const LEARNER_FIELDS = {
  id: 'id',
  lastName: 'last_name',
};

export class LearnerPaginationOptions extends TableQueryParams {
  constructor(data) {
    super(data);
    this.search = data.search;

    /** @type {User} */
    this.groupAdmin = data.groupAdmin;
  }
}

/**
 * Get courses.
 * @param {object} data Data.
 * @param {object} data.learner Learner.
 * @param {User} data.groupAdmin Group Admin.
 * @returns {Promise<User>}
 */
async function createNewLearner({ learner, groupAdmin }) {
  return catchHttpErrorWithValidationSupport(learnerMapper, async () => {
    const response = await axiosService.post(appUrlsConfig.learner.create, learnerMapper.toDto({ learner, groupAdmin }));
    return learnerMapper.fromDto(response.data);
  });
}

/**
 * Import learners.
 * @param {LearnerImport} data Data.
 * @returns {Promise<number[]>}
 */
async function importLearners(data) {
  const dataDto = learnerImportMapper.toDto(data);
  return catchHttpErrorWithValidationSupport(
    importFileMapper,
    async () => {
      const response = await axiosService.post(appUrlsConfig.learner.importLearners, dataDto);
      return response.data.data;
    },
  );
}

/**
 * Get learners.
 * @param {object} data Data.
 * @param {number} data.packageId Package id.
 * @param {User} data.groupAdmin Group admin.
 * @param {User[]} [data.excludedUsers] Excluded users.
 * @param {TableQueryParams} [data.options] Pagination options.
 * @returns {Promise<PaginationInfo>}
 */
async function getLearners({
  packageId, groupAdmin, excludedUsers, options,
}) {
  const response = await axiosService.get(appUrlsConfig.learner.get(packageId), {
    params: {
      ...learnerMapper.toLearnersQueryParamDto({ groupAdmin, excludedUsers }),
      ...queryParamsMapper.toDto(options, LEARNER_FIELDS),
      name: options.searchString,
      visibility: options.visibility?.value,
    },
  });
  return paginationInfoMapper.fromDto(response.data, learnerMapper.fromDto);
}

/**
 * Deactivate learners.
 * @param {object} data Learner.
 * @param {number[]} data.learnerIds Learner IDs.
 * @returns {Promise<void>}
 */
async function deactivateLearners({ learnerIds }) {
  await axiosService.put(appUrlsConfig.learner.deactivate, {
    learner_ids: learnerIds,
  });
}

/**
 * Activate learners.
 * @param {object} data Learner.
 * @param {number[]} data.learnerIds Learner IDs.
 * @returns {Promise<void>}
 */
async function activateLearners({ learnerIds }) {
  await axiosService.put(appUrlsConfig.learner.activate, {
    learner_ids: learnerIds,
  });
}

/**
 * Hide learners.
 * @param {object} params Params.
 * @param {number[]} params.learnerIds Learner IDs.
 * @param {boolean} params.isVisible Whether learner is visible.
 * @returns {Promise<void>}.
 */
async function changeLearnersVisibility({ learnerIds, isVisible }) {
  await axiosService.put(appUrlsConfig.learner.display, {
    learner_ids: learnerIds,
    visible: isVisible,
  });
}

/**
/* Export learners import template.
 * @returns {Promise<ExportExcel>}
 */
async function exportLearnersImportTemplate() {
  const response = await axiosService.get(appUrlsConfig.learner.exportImportLearnersTemplate);
  return exportExcelMapper.fromDto(response.data);
}

/**
 * Get pagination learners.
 * @param {LearnerPaginationOptions} options Query params options.
 * @returns {Promise<PaginationInfo>}
 */
async function getPaginationLearners(options) {
  const params = {
    ...prepareTableQueryParams({ options }),
    ...queryParamsMapper.toDto(options),
    ...managerRequestMapper.toDto({ groupAdmin: options.groupAdmin }),
    name: options.search,
  };

  const response = await axiosService.get(appUrlsConfig.learner.getPagination, { params });
  return paginationInfoMapper.fromDto(response.data, learnerMapper.fromDto);
}

/**
 * Delete learner.
 * @param {Learner} learner Learner
 * @returns {Promise<void>}
 */
async function deleteLearner(learner) {
  const response = await axiosService.delete(appUrlsConfig.learner.delete(learner.id));
  return response.data;
}

export const learnerService = {
  createNewLearner,
  getLearners,
  getPaginationLearners,
  deleteLearner,
  importLearners,
  exportLearnersImportTemplate,
  deactivateLearners,
  activateLearners,
  changeLearnersVisibility,
};
